/**
 * Sovereign Ambient Chronos Engine
 * Dynamically modulates UI color temperature, ambient phosphor glow radii,
 * and cognitive contrast profiles according to the solar circadian cycle.
 */

const STORAGE_KEY = 'memark_circadian_chronos';

export const CHRONOS_PHASES = {
  DAWN: {
    id: 'DAWN',
    name: 'Dawn Emerald',
    icon: '🌅',
    hours: [5, 6, 7, 8],
    accent: '#10b981',
    glow: 'rgba(16, 185, 129, 0.25)',
    bgGradient: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.12) 0%, rgba(5, 15, 10, 0.95) 75%)',
    description: 'Auroral low-glare green for cognitive awakening and fresh morning reading.',
  },
  SOLAR: {
    id: 'SOLAR',
    name: 'Solar Cyan',
    icon: '☀️',
    hours: [9, 10, 11, 12, 13, 14, 15, 16],
    accent: '#06b6d4',
    glow: 'rgba(6, 182, 212, 0.22)',
    bgGradient: 'radial-gradient(ellipse at 50% 0%, rgba(6, 182, 212, 0.1) 0%, rgba(5, 12, 24, 0.95) 75%)',
    description: 'High-contrast solar cyan calibrated for high-velocity midday bookmark curation.',
  },
  DUSK: {
    id: 'DUSK',
    name: 'Dusk Amber',
    icon: '🌆',
    hours: [17, 18, 19, 20],
    accent: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.25)',
    bgGradient: 'radial-gradient(ellipse at 50% 0%, rgba(245, 158, 11, 0.12) 0%, rgba(20, 12, 5, 0.95) 75%)',
    description: 'Warm amber twilight tone protecting circadian melatonin rhythm during evening study.',
  },
  MIDNIGHT: {
    id: 'MIDNIGHT',
    name: 'Midnight Void',
    icon: '🌌',
    hours: [21, 22, 23, 0, 1, 2, 3, 4],
    accent: '#a855f7',
    glow: 'rgba(168, 85, 247, 0.25)',
    bgGradient: 'radial-gradient(ellipse at 50% 0%, rgba(168, 85, 247, 0.12) 0%, rgba(8, 4, 16, 0.98) 75%)',
    description: 'Deep obsidian violet spectrum minimizing ocular fatigue during late-night hacking.',
  },
};

/**
 * Determine current circadian phase from system time or specified hour
 */
export function getCircadianPhase(overrideHour = null) {
  const currentHour = overrideHour !== null ? overrideHour : new Date().getHours();

  for (const phase of Object.values(CHRONOS_PHASES)) {
    if (phase.hours.includes(currentHour)) {
      return { ...phase, currentHour };
    }
  }

  return { ...CHRONOS_PHASES.MIDNIGHT, currentHour };
}

export function isCircadianEnabled() {
  if (typeof window === 'undefined') return true;
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    return val === null ? true : val === 'true';
  } catch {
    return true;
  }
}

export function setCircadianEnabled(enabled) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch (e) {
    console.warn('Failed to set circadian preference', e);
  }
}

/**
 * Inject dynamic circadian CSS custom properties into document root
 */
export function applyCircadianTheme(phase) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!phase) return;

  root.style.setProperty('--chronos-accent', phase.accent);
  root.style.setProperty('--chronos-glow', phase.glow);
}
